import { useEffect, useState } from "react";
import { DatabaseBackup, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { fmtDateTime } from "../utils/format";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [customCats, setCustomCats] = useState([]);
  const [catForm, setCatForm] = useState({ group: "", category: "" });

  const loadCats = () => api.get("/expense-categories").then((r) => setCustomCats(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    loadCats();
  }, []);

  if (!settings) return <div className="flex h-96 items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-4 border-[#2563EB] border-t-transparent" /></div>;

  const rates = settings.rates || { USD: 600, EUR: 655 };
  const company = settings.company || { name: "", address: "", phone: "" };

  const refreshRates = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/rates/refresh");
      setSettings((s) => ({ ...s, rates: r.data.rates, rates_updated_at: r.data.rates_updated_at, rates_source: r.data.rates_source }));
      toast.success(`Taux actualisés : 1 USD = ${r.data.rates.USD} FCFA · 1 EUR = ${r.data.rates.EUR} FCFA`);
    } catch (e) { toast.error(fmtErr(e)); } finally { setRefreshing(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        rates, company,
        rates_auto: settings.rates_auto !== false,
        min_margin_pct: settings.min_margin_pct,
        big_expense_threshold: settings.big_expense_threshold,
        require_receipt: settings.require_receipt,
        approval_thresholds: settings.approval_thresholds,
      });
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(fmtErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Paramètres</h1>
        <p className="mt-1 text-sm text-slate-500">Devise principale : FCFA. Taux utilisés pour convertir les achats en USD/EUR.</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-heading text-base font-semibold text-slate-800">Taux de change (vers FCFA)</h3>
          <Button variant="outline" size="sm" data-testid="rates-refresh-button" onClick={refreshRates} disabled={refreshing}>
            <RefreshCw className={`mr-1 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Actualiser les taux
          </Button>
        </div>
        <p className="text-xs text-slate-400" data-testid="rates-updated-at">
          {settings.rates_updated_at
            ? `Dernière mise à jour : ${fmtDateTime(settings.rates_updated_at)} — source : ${settings.rates_source || "—"}`
            : "Taux jamais actualisés automatiquement — valeurs manuelles."}
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>1 USD = ? FCFA</Label>
            <Input type="number" value={rates.USD} data-testid="rate-usd-input"
              onChange={(e) => setSettings({ ...settings, rates: { ...rates, USD: e.target.value } })} />
          </div>
          <div>
            <Label>1 EUR = ? FCFA</Label>
            <Input type="number" value={rates.EUR} data-testid="rate-eur-input"
              onChange={(e) => setSettings({ ...settings, rates: { ...rates, EUR: e.target.value } })} />
          </div>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" className="h-4 w-4 accent-cyan-600" data-testid="rates-auto-checkbox"
            checked={settings.rates_auto !== false}
            onChange={(e) => setSettings({ ...settings, rates_auto: e.target.checked })} />
          Actualisation automatique quotidienne des taux (6h UTC)
        </label>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
        <h3 className="font-heading text-base font-semibold text-slate-800">Informations de l'entreprise (factures PDF)</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Nom de l'entreprise</Label><Input value={company.name || ""} data-testid="company-name-input"
            onChange={(e) => setSettings({ ...settings, company: { ...company, name: e.target.value } })} /></div>
          <div><Label>Téléphone</Label><Input value={company.phone || ""} data-testid="company-phone-input"
            onChange={(e) => setSettings({ ...settings, company: { ...company, phone: e.target.value } })} /></div>
          <div><Label>WhatsApp</Label><Input value={company.whatsapp || ""}
            onChange={(e) => setSettings({ ...settings, company: { ...company, whatsapp: e.target.value } })} /></div>
          <div><Label>E-mail</Label><Input value={company.email || ""}
            onChange={(e) => setSettings({ ...settings, company: { ...company, email: e.target.value } })} /></div>
          <div><Label>RCCM</Label><Input value={company.rccm || ""}
            onChange={(e) => setSettings({ ...settings, company: { ...company, rccm: e.target.value } })} /></div>
          <div><Label>NIF</Label><Input value={company.nif || ""}
            onChange={(e) => setSettings({ ...settings, company: { ...company, nif: e.target.value } })} /></div>
          <div className="col-span-2"><Label>Adresse</Label><Input value={company.address || ""} data-testid="company-address-input"
            onChange={(e) => setSettings({ ...settings, company: { ...company, address: e.target.value } })} /></div>
          <div className="col-span-2"><Label>Conditions de vente</Label><Textarea rows={2} value={company.conditions_vente || ""}
            onChange={(e) => setSettings({ ...settings, company: { ...company, conditions_vente: e.target.value } })} /></div>
          <div className="col-span-2"><Label>Conditions de location</Label><Textarea rows={2} value={company.conditions_location || ""}
            onChange={(e) => setSettings({ ...settings, company: { ...company, conditions_location: e.target.value } })} /></div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
        <h3 className="font-heading text-base font-semibold text-slate-800">Dépenses : validation & budgets</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Marge minimale entreprise (%)</Label>
            <Input type="number" value={settings.min_margin_pct ?? 10} data-testid="min-margin-input"
              onChange={(e) => setSettings({ ...settings, min_margin_pct: Number(e.target.value) })} />
            <p className="mt-1 text-xs text-slate-400">Sert au calcul du prix minimum de vente conseillé.</p>
          </div>
          <div>
            <Label>Seuil « dépense importante » (FCFA)</Label>
            <Input type="number" value={settings.big_expense_threshold ?? 1000000} data-testid="big-expense-input"
              onChange={(e) => setSettings({ ...settings, big_expense_threshold: Number(e.target.value) })} />
          </div>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" className="h-4 w-4 accent-cyan-600" data-testid="require-receipt-checkbox"
            checked={!!settings.require_receipt}
            onChange={(e) => setSettings({ ...settings, require_receipt: e.target.checked })} />
          Justificatif obligatoire pour valider une dépense
        </label>
        <div>
          <Label className="mb-2 block">Seuils d'autorisation par rôle (FCFA) — au-delà : validation directeur/admin</Label>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {[["commercial", "Commercial"], ["gestionnaire", "Gestionnaire"], ["comptable", "Comptable"], ["direction", "Direction"]].map(([role, label]) => (
              <div key={role}>
                <Label className="text-xs">{label}</Label>
                <Input type="number" value={(settings.approval_thresholds || {})[role] ?? ""} placeholder="défaut"
                  data-testid={`threshold-${role}`}
                  onChange={(e) => setSettings({ ...settings, approval_thresholds: { ...(settings.approval_thresholds || {}), [role]: Number(e.target.value) || 0 } })} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-3" data-testid="categories-manager">
        <h3 className="font-heading text-base font-semibold text-slate-800">Catégories de dépenses personnalisées</h3>
        <div className="flex flex-wrap gap-2">
          {customCats.map((c) => (
            <span key={c.id} className="flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs">
              <b>{c.group}</b> / {c.category}
              <button className="text-red-400 hover:text-red-600" onClick={async () => { await api.delete(`/expense-categories/${c.id}`); loadCats(); }} data-testid={`delete-cat-${c.id}`}>×</button>
            </span>
          ))}
          {customCats.length === 0 && <p className="text-sm text-slate-400">Aucune catégorie personnalisée.</p>}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div><Label className="text-xs">Groupe (existant ou nouveau)</Label><Input value={catForm.group} onChange={(e) => setCatForm({ ...catForm, group: e.target.value })} placeholder="Ex : Réparation" data-testid="cat-group-input" /></div>
          <div><Label className="text-xs">Sous-catégorie</Label><Input value={catForm.category} onChange={(e) => setCatForm({ ...catForm, category: e.target.value })} placeholder="Ex : Turbo" data-testid="cat-category-input" /></div>
          <Button variant="outline" data-testid="cat-add-button" onClick={async () => {
            try {
              await api.post("/expense-categories", catForm);
              toast.success("Catégorie ajoutée");
              setCatForm({ group: "", category: "" });
              loadCats();
            } catch (e) { toast.error(fmtErr(e)); }
          }}>Ajouter</Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-3">
        <h3 className="font-heading text-base font-semibold text-slate-800">Sauvegarde des données</h3>
        <p className="text-sm text-slate-500">Téléchargez une copie complète de la base de données (JSON) : véhicules, ventes, crédits, comptabilité, utilisateurs…</p>
        <Button variant="outline" data-testid="backup-download-button" onClick={async () => {
          const res = await api.get("/backup", { responseType: "blob" });
          const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
          const a = document.createElement("a");
          a.href = url;
          a.download = `backup-sylla-auto-good-${new Date().toISOString().slice(0, 10)}.json`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        }}>
          <DatabaseBackup className="mr-1 h-4 w-4" /> Télécharger la sauvegarde
        </Button>
      </div>

      <Button onClick={save} disabled={saving} data-testid="settings-save-button">
        <Save className="mr-1 h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer les paramètres"}
      </Button>
    </div>
  );
}
